import React, { useState, useEffect } from 'react';
import { pathTo } from '../../../Routes';
import { Redirect } from 'react-router-dom';
import { Translation } from 'react-i18next';
import Logger from '../../../../../lib/Logger';
import scrollToTop from '../../../elements/lib/scrollToTop';
import SmsConversationList from '../containers/SmsConversationListContainer';
import SmsMessageList from '../../smsMessages/containers/SmsMessageListContainer';
import SmsConversationProfile from '../containers/SmsConversationProfileContainer';
import DocumentHead from '../../../elements/components/DocumentHead';
import io from 'socket.io-client';
import api from '../../../../../state/api';
import Config from '../../../../../Config';
import Format from '../../../../../lib/Format';
import './styles/SmsConversationScreen.less';

const SmsConversationScreen = ({
  partnershipId,
  profileId,
  accessTabs,
  deletedConversationId,
  updateConversation,
  addMessage,
  reloadMessages,
  markAsRead,
  tz,
  ...props
}) => {
  const [redirect, setRedirect] = useState(null);
  const [selectedSmsConversation, setSelectedSmsConversation] = useState(null);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    scrollToTop();
  }, []);

  useEffect(() => {
    if (accessTabs === null || !accessTabs?.messages) {
      setRedirect('HomeScreen');
    }
  }, [accessTabs]);

  const handleSelectSmsConversation = (smsConversation) => {
    setSelectedSmsConversation(smsConversation);
    setIsProfileOpen(false);
  };

  const handleOpenProfile = () => {
    setIsProfileOpen(!isProfileOpen);
  };

  const handleCloseProfile = () => {
    setIsProfileOpen(false);
  };

  const smsConversationName = (smsConversation) => {
    if (
      smsConversation?.profile?.first_name &&
      smsConversation?.profile?.last_name
    ) {
      return `${smsConversation?.profile?.first_name} ${smsConversation?.profile?.last_name}`;
    }
    return Format.telephone(smsConversation.target_phone_number || '');
  };

  useEffect(() => {
    document.body.classList.add('sms-conversation-mode');
    return () => document.body.classList.remove('sms-conversation-mode');
  }, []);

  useEffect(() => {
    if (
      selectedSmsConversation &&
      deletedConversationId &&
      selectedSmsConversation.id === deletedConversationId
    ) {
      setSelectedSmsConversation(null);
      setIsProfileOpen(false);
    }
  }, [deletedConversationId, selectedSmsConversation]);

  useEffect(() => {
    if (!partnershipId) return;

    const socketId = `sms_conversations_${partnershipId}`;
    const socket = io(Config.get('API_BASE_URL_SOCKET'), {
      extraHeaders: {
        Authorization: api._getAuthToken(),
      },
      query: {
        app_key: api.app_key,
      },
    });

    socket.on(socketId, (data) => {
      const { sms_message, sms_conversation, extra_message } = data;

      if (sms_message && sms_conversation) {
        // Convert sent_at to ISO string for Redux storage
        // Redux cannot properly serialize moment objects
        const messageForRedux = {
          ...sms_message,
          sent_at:
            typeof sms_message.sent_at === 'string'
              ? sms_message.sent_at
              : sms_message.sent_at?._i || new Date().toISOString(),
        };

        const isViewingConversation =
          selectedSmsConversation?.id === sms_conversation.id;

        // Check if the message was sent by the current user (no alert needed)
        // For system events (kind=2), check event_actor_profile instead of profile
        const isSentByMe =
          sms_message.kind === 2
            ? sms_message.event_actor_profile?.id === profileId
            : sms_message.profile?.id === profileId;

        updateConversation(
          partnershipId,
          profileId,
          sms_conversation,
          messageForRedux,
          isViewingConversation,
          isSentByMe
        );

        // Handle extra_message if present (usually system events)
        if (extra_message) {
          const extraMessageForRedux = {
            ...extra_message,
            sent_at:
              typeof extra_message.sent_at === 'string'
                ? extra_message.sent_at
                : extra_message.sent_at?._i || new Date().toISOString(),
          };

          // For system events (kind=2), check event_actor_profile instead of profile
          const isExtraSentByMe =
            extra_message.event_actor_profile?.id === profileId;

          updateConversation(
            partnershipId,
            profileId,
            sms_conversation,
            extraMessageForRedux,
            isViewingConversation,
            isExtraSentByMe
          );
        }
        if (isViewingConversation) {
          reloadMessages(
            partnershipId,
            sms_conversation.id,
            1,
            20,
            'sent_at.desc'
          );
          // Don't mark system events (kind=2) as read automatically
          if (sms_message.kind !== 2) {
            markAsRead(partnershipId, sms_conversation.id, {
              last_read_sms_message_id: messageForRedux.id,
            });
          }
        }
      }
    });

    return () => {
      socket.off('connect');
      socket.off('disconnect');
      socket.off(socketId);
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    partnershipId,
    updateConversation,
    addMessage,
    selectedSmsConversation,
    reloadMessages,
    markAsRead,
  ]);

  if (redirect) {
    return <Redirect to={pathTo(redirect)} />;
  }

  return (
    <Translation>
      {(t) => (
        <>
          <DocumentHead title={t('messages_list_navbar')} />

          <div className='sms-conversation-screen'>
            <div className='sms-conversation-layout'>
              <div className='sms-conversation-sidebar'>
                <SmsConversationList
                  onSelectSmsConversation={handleSelectSmsConversation}
                  selectedSmsConversation={selectedSmsConversation}
                  sortOrder={sortOrder}
                  onSortOrderChange={setSortOrder}
                  smsConversationName={smsConversationName}
                />
              </div>

              <div className='sms-conversation-main'>
                <SmsMessageList
                  selectedSmsConversation={selectedSmsConversation}
                  onOpenProfile={handleOpenProfile}
                  smsConversationName={smsConversationName}
                  isProfileOpen={isProfileOpen}
                />
              </div>

              {isProfileOpen && selectedSmsConversation && (
                <div className='sms-conversation-profile-panel'>
                  <SmsConversationProfile
                    smsConversation={selectedSmsConversation}
                    onClose={handleCloseProfile}
                  />
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </Translation>
  );
};

export default SmsConversationScreen;

Logger.log('silly', `SmsConversationScreen loaded.`);
